<template>
    <div v-if="maintenance" class="maintenance-screen">
        <i class="fa-solid fa-screwdriver-wrench maintenance-icon"></i>
        <h1>{{ $LANG.Get('labels.maintenanceTitle') }}</h1>
        <p>{{ $LANG.Get('labels.maintenanceMessage') }}</p>
    </div>
    <div v-else-if="$route.meta.public">
        <RouterView></RouterView>
    </div>
    <div v-else-if="sessionCheckDone" class="app-container">
        <NevsLoader v-if="$store.state.loaderCount > 0"></NevsLoader>
        <NevsNotification></NevsNotification>
        <LoginForm v-if="$store.state.user === null" :postLogin="afterLogin"></LoginForm>
        <template v-if="$store.state.user !== null">
            <TermsAcceptance v-if="!termsAccepted" @accepted="handleTermsAccepted"></TermsAcceptance>
            <template v-else>
            <Transition name="main-menu">
                <NevsMainMenu v-show="showMenu" :items="menu.items" :logo="menu.logo" :client-logo="menu.clientLogo"
                              @toggleMenu="showMenu=!showMenu"></NevsMainMenu>
            </Transition>
            <NevsTopBar :breadcrumbs="$store.state.breadcrumbs" :buttons="topBarButtons"
                        @toggleMenu="showMenu=!showMenu"></NevsTopBar>
            <!-- Check-in / check-out from the top bar, so it is reachable from any screen. -->
            <ModalCheckMember :show="checkMemberModal" @done="checkMemberDone"></ModalCheckMember>
            <!-- The shared sticky-note board, likewise reachable from anywhere. -->
            <NotesOverlay :show="notesOverlay" @done="notesOverlay = false"></NotesOverlay>
            <div class="nevs-main-content">
                <div v-if="$store.state.user.financial_status_id === 2" class="financial-warning">
                    {{ $LANG.Get('alerts.accountWarned') }}
                </div>
                <div v-if="$store.state.user.financial_status_id !== 2" class="financial-warning-placeholder"></div>
                <div v-if="maintenanceDate !== null" class="maintenance-planned-warning">
                    {{ maintenancePlannedText }}
                </div>
                <div v-if="overdueInvoices.length > 0" class="overdue-invoices-warning">
                    {{ overdueInvoicesText[0] }}<template v-for="(invoice, index) in overdueInvoices" :key="invoice.id"><span
                        v-if="index > 0">, </span><a href="#" class="overdue-invoice-link"
                        @click.prevent="openOverdueInvoicePdf(invoice.id)">{{ invoice.number }}</a></template>{{ overdueInvoicesText[1] }}
                </div>
                <div v-show="!$router.currentRoute.value.path.startsWith('/messages')" v-for="(message, key) in messages" :key="key" class="unread-message">
                    <NevsButton @click="openMessage(message.id)"><i class="fa-solid fa-envelope-open"></i> {{ $LANG.Get('buttons.openMessage') }}</NevsButton>
                    {{ $LANG.Get('labels.newMessage') }} - {{ message.title }}
                    <span class="unread-message-time">({{ message.time_display }})</span>
                </div>
                <RouterView></RouterView>
            </div>
            </template>
        </template>
        <NevsPopup></NevsPopup>
    </div>
</template>

<script>

import NevsLoader from "@/components/nevs/NevsLoader.vue";
import NevsNotification from "@/components/nevs/NevsNotification.vue";
import NevsPopup from "@/components/nevs/NevsPopup.vue";
import LoginForm from "@/components/general/LoginForm.vue";
import TermsAcceptance from "@/components/general/TermsAcceptance.vue";
import NevsMainMenu from "@/components/nevs/NevsMainMenu.vue";
import NevsTopBar from "@/components/nevs/NevsTopBar.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import ModalCheckMember from "@/components/modals/ModalCheckMember.vue";
import NotesOverlay from "@/components/general/NotesOverlay.vue";

export default {
    name: 'App',
    components: {
        ModalCheckMember,
        NotesOverlay,
        NevsButton,
        NevsMainMenu,
        NevsTopBar,
        LoginForm,
        TermsAcceptance,
        NevsPopup,
        NevsNotification,
        NevsLoader
    },
    data() {
        return {
            maintenance: false,
            maintenanceDate: null,
            maintenanceTime: null,
            maintenanceHours: null,
            showMenu: true,
            checkMemberModal: false,
            notesOverlay: false,
            sessionCheckDone: false,
            menu: {
                logo: import.meta.env.BASE_URL + 'logo.png',
                // The client's uploaded logo (empty string when none is set), shown above the product logo.
                clientLogo: '',
                items: []
            },
            topBarButtons: [
                {
                    icon: '<i class="fa-solid fa-person-walking-arrow-right"></i>',
                    tooltip: this.$LANG.Get('tooltips.checkInMember'),
                    action: () => {
                        this.checkMemberModal = true;
                    }
                },
                {
                    icon: '<i class="fa-solid fa-note-sticky"></i>',
                    tooltip: this.$LANG.Get('tooltips.notes'),
                    action: () => {
                        this.notesOverlay = true;
                    }
                },
                {
                    icon: '<i class="fa-solid fa-envelope"></i>',
                    tooltip: this.$LANG.Get('tooltips.messages'),
                    action: () => {
                        this.$router.push('/messages');
                    }
                },
                {
                    icon: '<i class="fa-solid fa-user"></i>',
                    tooltip: this.$LANG.Get('tooltips.myProfile'),
                    action: () => {
                        this.$router.push('/users/' + this.$store.state.user.id);
                    }
                },
                {
                    icon: '<i class="fa-solid fa-right-from-bracket"></i>',
                    tooltip: this.$LANG.Get('tooltips.logout'),
                    action: () => {
                        let vm = this;
                        this.$API.APICall('post', 'logout', {}, (data, success) => {
                            if (success) {
                                vm.$store.commit('setUser', null);
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            ],
            messages: [],
            overdueInvoices: [],
            crossTabHandle: null
        }
    },
    computed: {
        termsAccepted() {
            // A logged-in user must have accepted the current terms version before the app is shown. Both values
            // come from the session data (accepted `terms` column vs current `latest_terms` from terms.json).
            const user = this.$store.state.user;
            if (user === null) return true;
            return String(user.terms) === String(user.latest_terms);
        },
        maintenancePlannedText() {
            if (this.maintenanceDate === null) return '';
            const dateParts = this.maintenanceDate.split('-');
            const formattedDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}.${dateParts[0]}.`;
            const formattedTime = this.maintenanceTime ? this.maintenanceTime.substring(0, 5) : '';
            return this.$LANG.Get('labels.maintenancePlanned')
                .replace('%date%', formattedDate)
                .replace('%time%', formattedTime)
                .replace('%hours%', this.maintenanceHours);
        },
        overdueInvoicesText() {
            // The text before and after the invoice numbers, which are rendered in between as links.
            const parts = this.$LANG.Get('alerts.overdueInvoices').split('%invoices%');
            return [parts[0], parts.slice(1).join('')];
        }
    },
    methods: {
        afterLogin() {
            this.setupMenu();
            this.checkOverdueInvoices();
        },
        handleTermsAccepted() {
            // The terms gate updates the store user itself; make sure the menu is ready for the app that follows.
            this.setupMenu();
        },
        checkMemberDone(success) {
            this.checkMemberModal = false;
            // The dashboard grids listen for this so a check-in made from another screen (or while the
            // dashboard is open behind the modal) shows up right away.
            if (success) {
                this.$store.commit('bumpVisitsVersion');
            }
        },
        openMessage(messageId) {
            this.$router.push('/messages/' + messageId);
        },
        resolveWindowResize() {
            this.showMenu = window.innerWidth >= 800;
        },
        setupMenu() {
            // Show the client's uploaded logo at the top of the menu when one is set. The upload endpoint is
            // cookie-authenticated like the rest of the app, so the stored link can be used directly in an <img>.
            const clientLogo = this.$store.state.user?.client?.logo;
            this.menu.clientLogo = (clientLogo && clientLogo.link) ? clientLogo.link : '';

            this.menu.items = [];
            this.menu.items.push({
                id: 'dashboard',
                label: this.$LANG.Get('modules.dashboard'),
                link: '/',
                icon: '<i class="fa-solid fa-tachometer-alt"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'clients',
                label: this.$LANG.Get('modules.clients'),
                link: null,
                icon: '<i class="fa-solid fa-address-book"></i>',
                children: [
                    {
                        id: 'persons',
                        label: this.$LANG.Get('modules.persons'),
                        link: '/persons',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'companies',
                        label: this.$LANG.Get('modules.companies'),
                        link: '/companies',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'registrations',
                        label: this.$LANG.Get('modules.registrations'),
                        link: '/registrations',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'groups',
                        label: this.$LANG.Get('modules.groups'),
                        link: '/groups',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'notifications',
                        label: this.$LANG.Get('modules.notifications'),
                        link: '/notifications',
                        icon: '',
                        children: [],
                        permissions: ['ADMINISTRATOR', 'NOTIFICATIONS']
                    }
                ]
            });
            let catalogChildren = [
                {
                    id: 'products',
                    label: this.$LANG.Get('modules.products'),
                    link: '/products',
                    icon: '',
                    children: []
                },
                {
                    id: 'memberships',
                    label: this.$LANG.Get('modules.memberships'),
                    link: '/memberships',
                    icon: '',
                    children: []
                },
                {
                    id: 'packages',
                    label: this.$LANG.Get('modules.packages'),
                    link: '/packages',
                    icon: '',
                    children: []
                }
            ];
            if (this.$store.state.user.client.plus) {
                catalogChildren.push({
                    id: 'wares',
                    label: this.$LANG.Get('modules.wares'),
                    link: '/wares',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                });
                catalogChildren.push({
                    id: 'receiptNotes',
                    label: this.$LANG.Get('modules.receiptNotes'),
                    link: '/receipt-notes',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                });
                catalogChildren.push({
                    id: 'writeOffNotes',
                    label: this.$LANG.Get('modules.writeOffNotes'),
                    link: '/write-off-notes',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                });
                catalogChildren.push({
                    id: 'supplies',
                    label: this.$LANG.Get('modules.supplies'),
                    link: '/supplies',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                });
            }
            this.menu.items.push({
                id: 'catalog',
                label: this.$LANG.Get('modules.catalog'),
                link: null,
                icon: '<i class="fa-solid fa-tags"></i>',
                children: catalogChildren
            });
            let financeChildren = [
                {
                    id: 'offers',
                    label: this.$LANG.Get('modules.offers'),
                    link: '/offers',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                },
                {
                    id: 'invoices',
                    label: this.$LANG.Get('modules.invoices'),
                    link: '/invoices',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                },
                {
                    id: 'payments',
                    label: this.$LANG.Get('modules.payments'),
                    link: '/payments',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                }
            ];
            if (this.$store.state.user.client.payment_plans_enabled) {
                financeChildren.push({
                    id: 'paymentPlans',
                    label: this.$LANG.Get('modules.paymentPlans'),
                    link: '/payment-plans',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                });
            }
            if (this.$store.state.user.client.plus) {
                financeChildren.push({
                    id: 'incomingInvoices',
                    label: this.$LANG.Get('modules.incomingInvoices'),
                    link: '/incoming-invoices',
                    icon: '',
                    children: [],
                    permissions: ['ADMINISTRATOR', 'FINANCES']
                });
            }
            this.menu.items.push({
                id: 'finance',
                label: this.$LANG.Get('modules.finance'),
                link: null,
                icon: '<i class="fa-solid fa-coins"></i>',
                children: financeChildren
            });
            this.menu.items.push({
                id: 'training',
                label: this.$LANG.Get('modules.training'),
                link: null,
                icon: '<i class="fa-solid fa-dumbbell"></i>',
                children: [
                    {
                        id: 'rooms',
                        label: this.$LANG.Get('modules.rooms'),
                        link: '/rooms',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'sessionTypes',
                        label: this.$LANG.Get('modules.sessionTypes'),
                        link: '/session-types',
                        icon: '',
                        children: []
                    },
                    {
                        id: 'calendar',
                        label: this.$LANG.Get('modules.calendar'),
                        link: '/calendar',
                        icon: '',
                        children: []
                    }
                ]
            });
            this.menu.items.push({
                id: 'statistics',
                label: this.$LANG.Get('modules.statistics'),
                link: null,
                icon: '<i class="fa-solid fa-chart-column"></i>',
                children: [
                    {
                        id: 'overviewStatistics',
                        label: this.$LANG.Get('modules.overviewStatistics'),
                        link: '/statistics-overview',
                        icon: '',
                        children: [],
                        permissions: ['ADMINISTRATOR', 'STATISTICS']
                    },
                    {
                        id: 'dailyReport',
                        label: this.$LANG.Get('modules.dailyReport'),
                        link: '/daily-report',
                        icon: '',
                        children: [],
                        permissions: ['ADMINISTRATOR', 'STATISTICS', 'FINANCES']
                    },
                    {
                        id: 'memberStatistics',
                        label: this.$LANG.Get('modules.memberStatistics'),
                        link: '/member-statistics',
                        icon: '',
                        children: [],
                        permissions: ['ADMINISTRATOR', 'STATISTICS']
                    },
                    {
                        id: 'financialStatistics',
                        label: this.$LANG.Get('modules.financialStatistics'),
                        link: '/financial-statistics',
                        icon: '',
                        children: [],
                        permissions: ['ADMINISTRATOR', 'STATISTICS']
                    }
                ]
            });
            if (this.$store.state.user.permissions.includes('ADMINISTRATOR')) {
                this.menu.items.push({
                    id: 'settings',
                    label: this.$LANG.Get('modules.settings'),
                    link: null,
                    icon: '<i class="fa-solid fa-cog"></i>',
                    children: [
                        {
                            id: 'generalData',
                            label: this.$LANG.Get('modules.generalData'),
                            link: '/general-data',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'pdfSettings',
                            label: this.$LANG.Get('modules.pdfSettings'),
                            link: '/pdf-settings',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'financialSettings',
                            label: this.$LANG.Get('modules.financialSettings'),
                            link: '/financial-settings',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'emailSettings',
                            label: this.$LANG.Get('modules.emailSettings'),
                            link: '/email-settings',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'workingHours',
                            label: this.$LANG.Get('modules.workingHours'),
                            link: '/working-hours',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'additionalScreens',
                            label: this.$LANG.Get('modules.additionalScreens'),
                            link: '/additional-screens',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'membersSettings',
                            label: this.$LANG.Get('modules.members'),
                            link: '/members-settings',
                            icon: '',
                            children: []
                        },
                        {
                            id: 'users',
                            label: this.$LANG.Get('modules.users'),
                            link: '/users',
                            icon: '',
                            children: []
                        }
                    ]
                });
                // The Intis registrations log is only relevant when the Intis integration is enabled for
                // this client, so its settings entry is shown only then.
                if (this.$store.state.user.client.intis_enabled) {
                    this.menu.items[this.menu.items.length - 1].children.push({
                        id: 'intisRegistrations',
                        label: this.$LANG.Get('modules.intisRegistrations'),
                        link: '/intis-registrations',
                        icon: '',
                        children: []
                    });
                }
                // Likewise the Nuki door log: it only has anything to show once the club has a smart lock
                // set up, so the entry appears only for clubs that do.
                if (this.$store.state.user.client.nuki_enabled) {
                    this.menu.items[this.menu.items.length - 1].children.push({
                        id: 'nukiRegistrations',
                        label: this.$LANG.Get('modules.nukiRegistrations'),
                        link: '/nuki-registrations',
                        icon: '',
                        children: []
                    });
                }
            }
            this.menu.items.push({
                id: 'assistance',
                label: this.$LANG.Get('modules.assistance'),
                link: '/assistance',
                icon: '<i class="fa-solid fa-headset"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'help',
                label: this.$LANG.Get('modules.help'),
                link: '/help',
                icon: '<i class="fa-solid fa-suitcase-medical"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'termsAndConditions',
                label: this.$LANG.Get('modules.termsAndConditions'),
                external: true,
                link: 'https://fytlix.com/uvjeti_koristenja.pdf',
                icon: '<i class="fa-solid fa-file-contract"></i>',
                children: []
            });
            this.menu.items.push({
                id: 'gdpr',
                label: this.$LANG.Get('modules.gdpr'),
                external: true,
                link: 'https://fytlix.com/GDPR.pdf',
                icon: '<i class="fa-solid fa-user-secret"></i>',
                children: []
            });

            // Enforce menu item permissions and hide empty categories. Each restricted item
            // carries a `permissions` array; the user needs at least one of them to see it.
            this.menu.items = this.menu.items.filter((item) => {
                if (Array.isArray(item.children) && item.children.length > 0) {
                    item.children = item.children.filter((child) => {
                        return child.permissions === undefined
                            || this.$HELPERS.HasAnyPermission(this, child.permissions);
                    });
                }
                if (item.permissions !== undefined && !this.$HELPERS.HasAnyPermission(this, item.permissions)) {
                    return false;
                }
                // A category (an item without its own link) with no visible children is hidden.
                if (item.link === null && (!Array.isArray(item.children) || item.children.length === 0)) {
                    return false;
                }
                return true;
            });
        },
        checkMessages() {
            let vm = this;
            this.$API.APICall('get', 'messages/unseen', {}, (data, success) => {
                if (success) {
                    vm.messages = data.messages;
                }
            });
        },
        checkOverdueInvoices() {
            // Fytlix's own invoices to this client that are unpaid past their due date, from the dashboard.
            let vm = this;
            this.$API.APICall('get', 'overdue-invoices', {}, (data, success) => {
                if (success) {
                    vm.overdueInvoices = data.invoices;
                }
            }, false);
        },
        openOverdueInvoicePdf(id) {
            // Straight to the API, like the other downloads: the session cookie goes along, so the endpoint can
            // check the user is logged in. Opened synchronously, so no popup blocker gets in the way.
            window.open(this.$store.state.settings.API_BASE + 'overdue-invoices/pdf/' + encodeURIComponent(id), '_blank');
        }
    },
    mounted() {
        window.addEventListener('resize', this.resolveWindowResize);
        this.resolveWindowResize();
        let vm = this;
        this.$API.APICall('get', 'public/version', {}, (data, success) => {
            if (!success) {
                if (data && data.error === 'maintenance in progress') {
                    vm.maintenance = true;
                }
                return;
            }
            vm.maintenanceDate = data.maintenance_date ?? null;
            vm.maintenanceTime = data.maintenance_time ?? null;
            vm.maintenanceHours = data.maintenance_hours ?? null;
            if (data.version !== this.$HELPERS.GetCookie('fytlix_version')) {
                this.$HELPERS.SetCookie('fytlix_version', data.version);
                window.location.reload(true);
            }
        });
        this.$API.APICall('get', 'session', {}, (data, success) => {
            if (success) {
                // Before setUser, which is what puts the menu on screen: it reads its stored state as it is created.
                vm.$store.commit('setUserData', data.user_data);
                vm.$store.commit('setUser', data.user);
                vm.$store.commit('setLocale', data.user.locale);
                vm.$nextTick(() => {
                    vm.checkMessages();
                    vm.checkOverdueInvoices();
                    vm.setupMenu();
                });
            } else {
                vm.$store.commit('setLocale', vm.$store.state.settings.LOCALE);
            }
            vm.$nextTick(() => {
                vm.sessionCheckDone = true;
            });
        }, false);
        setInterval(() => {
            this.$API.APICall('get', 'public/heartbeat', {}, (data, success) => {
                if (!success) {
                    if (data && data.error === 'maintenance in progress') {
                        vm.maintenance = true;
                    }
                    return;
                }
                vm.maintenance = false;
                vm.maintenanceDate = data.maintenance_date ?? null;
                vm.maintenanceTime = data.maintenance_time ?? null;
                vm.maintenanceHours = data.maintenance_hours ?? null;
                if (vm.$store.state.user !== null) {
                    if (!data.logged_in) {
                        vm.$store.commit('setUser', null);
                    }
                    if (data.version !== this.$HELPERS.GetCookie('fytlix_version')) {
                        this.$HELPERS.SetCookie('fytlix_version', data.version);
                        window.location.reload(true);
                    }
                }
            }, false);
        }, 30000);
        this.crossTabHandle = this.$CROSS_TAB_BUS.ListenToEvent('check-messages', () => {
            vm.checkMessages();
        });
    },
    unmounted() {
        if (this.crossTabHandle !== null) {
            this.$CROSS_TAB_BUS.UnbindEvent(this.crossTabHandle);
        }
    }
}

</script>

<style scoped>
.maintenance-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: #f4f6f8;
    text-align: center;
    padding: 20px;
}

.maintenance-icon {
    font-size: 80px;
    color: #999;
    margin-bottom: 30px;
}

.maintenance-screen h1 {
    font-size: 28px;
    color: #444;
    margin-bottom: 15px;
    font-weight: 600;
}

.maintenance-screen p {
    font-size: 16px;
    color: #777;
    max-width: 480px;
    line-height: 1.6;
}

@media only screen and (max-width: 1920px) and (-webkit-device-pixel-ratio: 1.25) {
    :deep(.nevs-main-menu) {
        font-size: 16px;
    }
}

.financial-warning {
    background: #F88379;
    padding: 10px;
    color: black;
}

.financial-warning-placeholder {
    height: 1px;
}

.maintenance-planned-warning {
    margin: 10px;
    background: #F88379;
    padding: 10px;
    color: black;
    border-radius: 10px;
}

.overdue-invoices-warning {
    margin: 10px;
    background: #F88379;
    padding: 10px;
    color: black;
    border-radius: 10px;
}

.overdue-invoice-link {
    color: black;
    font-weight: bold;
    text-decoration: underline;
}

.unread-message {
    margin: 10px;
    background: #FFAC1C;
    padding: 10px;
    color: black;
    border-radius: 10px;
}

.unread-message-time {
    font-style: italic;
    font-size: 12px;
}

/* Every grid in the app, from here rather than from each module: the routed views render inside elements that
   carry this component's scope attribute, so :deep reaches their tables however deep they sit.
   NevsActions renders a whole NevsField, so an actions cell stacks the table's cell padding on the field's own
   padding on the padding inside the control - about twice the height of the plain text cells beside it, and a
   row takes its tallest cell. Trimmed so the actions column stops setting the row height on its own. */
:deep(.nevs-table-actions) {
    padding-top: 4px;
    padding-bottom: 4px;
}

:deep(.nevs-table-actions .nevs-field) {
    padding: 0;
}

/* Vertical only: the trigger would look cramped without its horizontal padding. */
:deep(.nevs-table-actions .nevs-field-content) {
    padding-top: 5px;
    padding-bottom: 5px;
}
</style>